import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import {Link, useLocation, useHistory} from 'react-router-dom';
import Pagination from '@material-ui/lab/Pagination';
import PaginationItem from '@material-ui/lab/PaginationItem';
import Loading from '../components/Loading/Loading';
import {getArticleList} from '../sagas/newsArticles';
import {getNewsArticles} from '../actions';
import PageContainer from './PageContainer';
import styles from './News.scss';
import ArticleTab, {newsTypes} from '../components/News/ArticleTab';
import {breakpointMdHigh} from '../lib/config';
import {Helmet} from 'react-helmet';
import {extractSearchParams} from '../lib/urlTools';

const maxNumberOfItemsPerPage = 10;
const getFilterIndex = (queryString, filterType) => {
  let qs = queryString.replace(/\?/igm, '&').split('&');
  qs.shift();
  filterType = filterType.toLowerCase();
  const re = new RegExp(`${filterType}=`, 'ig');
  return {qs: qs, qsIndex: qs.findIndex((item) => {return item.match(re)})};
}

const newsURL = (queryString, filterType, filter='All') => {
  const {qs, qsIndex} = getFilterIndex(queryString, filterType);
  const {qs:ps, qsIndex:pageIndex} = getFilterIndex(queryString, 'page');
  if (pageIndex >= 0) qs.splice(pageIndex, 1); // reset page
  if (qsIndex >= 0) {
    if (filter == 'All') qs.splice(qsIndex, 1);
    else qs[qsIndex] = `${filterType}=${filter}`;
  }
  else {
    if (filter !== 'All') qs.push(`${filterType}=${filter}`);
  }
  return `/news?${qs.join('&')}`;
};

const RenderPaginationItem = (props) => {
  const location = useLocation();
  return (
    <PaginationItem
      component={Link}
      to={newsURL(location.search, 'page', props.page)}
      {...props}
    />
  );
};

function News(props) {
  const articleListRaw = useSelector(state => state.newsArticleList);
  const [articleList, setArticleList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentOption, setCurrentOption] = useState(0);
  const [page, setPage] = useState(1);
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    function toggleIsMobile() {
      setIsMobile(window.innerWidth <= breakpointMdHigh ? true : false);
    }
    window.addEventListener('resize', toggleIsMobile);
    toggleIsMobile();
    return () => window.removeEventListener('resize', toggleIsMobile);
  }, []);


  const getPaginationText = () => {
    if (!articleList) return '';
    const currentPageNumber = articleList.currentPage;
    const totalArticleCount = articleList.count;
    const showingPerPage = articleList.showing;

    let startingItemNumber = 1;
    let endingItemNumber = -1;

    startingItemNumber = (currentPageNumber -1)*maxNumberOfItemsPerPage +1;
    endingItemNumber = startingItemNumber + (showingPerPage - 1);

    if (endingItemNumber > totalArticleCount) {
      do {
        endingItemNumber = endingItemNumber - 1;
      } while(endingItemNumber > totalArticleCount);
    }

    const displayText = `Showing ${startingItemNumber} - ${endingItemNumber} out of ${totalArticleCount} results`;

    return displayText;
  };

  const getCurrentPage = () => {
    let retPage = 1;
    if (location?.search && location.search.match(/page=/)) {
      let newPage = parseInt(location.search.match(/page=(\d+)/)[1]);
      if (newPage != page) retPage = newPage;
    }
    return retPage;
  };

  const filterResults = (filter) => {
    if (filter?.type) {
      if (filter.type === 'All') history.push('/news')
      else history.push(newsURL(location.search, 'Type', filter.type));
    }
  };

  useEffect(() => {
    if (!articleListRaw || articleListRaw.status !== 'success') {
      setLoading(true);
      dispatch(getNewsArticles({search: location.search}));
    }
  }, []);

  useEffect(() => {
    let newPage = getCurrentPage();

    let doSearch = true;
    if (articleListRaw && articleListRaw.status === 'success' && articleListRaw.data.ssr) {
      if (loading) setLoading(false);
      doSearch = false;
      articleListRaw.data.ssr = false;
    }

    if (doSearch) {
      setLoading(true);
      dispatch(getNewsArticles({search: location.search}));
    }
    if (page != newPage) setPage(newPage);
  }, [location]);

  useEffect(() => {
    switch (articleListRaw.status) {
      case 'fetching':
      case 'loading':
        if (!loading) setLoading(true);
        break;
      case 'success':
        setArticleList(articleListRaw.data);
        if (page !== articleListRaw.data.currentPage) setPage(articleListRaw.data.currentPage);
        setLoading(false);
        break;
      default:
        if (loading) setLoading(false);
        break;
    }
  }, [articleListRaw]);

  // handle tab selection
  useEffect(() => {
    const searchType = extractSearchParams(props.location.search)?.Type;
    const searchTypeIndex = newsTypes.findIndex(newsType => newsType.type === searchType);
    const activeTab = searchTypeIndex === -1 ? 0 : searchTypeIndex;

    setCurrentOption(activeTab);
  }, [props.location?.search]);

  const handleFilterChange = (value) => {
    filterResults(value);
  };

  return (
    <PageContainer class="mui-bg">
      <Helmet>
        <title>Property News | Onthehouse.com.au</title>
        <meta name="description" content=" Read the latest property news and information on the Australian real estate market, designed for homeowners, buyers, sellers and investors." />
      </Helmet>
      <div className={styles.articleListBackground}>
        <div className={styles.articleListBackgroundOverlay}>
          <div className={`m-auto rounded-lg ${styles.articleListTitleOverlay}`}>
            <h1 id="articleListTitle" className={styles.articleListTitle}>
            Latest Property News
            </h1>
            <div className={styles.articleListSubLine}>
              <h4 className={styles.articleListSubTitle}>Read the latest real estate news and guides to help you with your property journey</h4>
            </div>
          </div>
        </div>
      </div>
      <div className="container d-flex flex-column align-items-center">
        <div className={styles.newsContainer}>
          {loading && <Loading />}
          <ArticleTab isMobile={isMobile} currentOption={currentOption} handleFilterChange={handleFilterChange} articles={articleList?.content ? articleList.content : []}/>
          {(articleListRaw.status === 'success' && (!articleList || !articleList?.content || (articleList?.content && articleList.content.length === 0))) && 
            <div className="mt-5 mb-5 text-center">There are no news articles with your selected Topic or Type.</div>
          }
          {articleList && articleList.totalPages > 1 &&
            <div className={`mt-4 ${styles.paginationContainer}`}>
              <span className={styles.paginationText}>{getPaginationText()}</span>
              <Pagination page={page} count={articleList.totalPages} renderItem={RenderPaginationItem} variant="outlined" color="primary" shape="rounded" />
            </div>
          }
        </div>
      </div>
    </PageContainer>
  );
}

function preloadSaga() {
  return [getArticleList];
}

News.preloadSaga = preloadSaga;

News.propTypes = {
  location: PropTypes.object,
};

export default News;

